/**
 * Message contract between the master's tab and the recorder window
 * (Version 2.md, D10 / D11).
 *
 * The recorder gets almost everything it composites natively, as a Daily
 * participant: the master's camera and mic come from the hardware via
 * useDirectDeviceCapture (D18), screen share and remote peers come from track
 * subscriptions, and the dominant speaker comes from 'active-speaker-change'.
 *
 * The quiz is the one exception — it is not a media track. The master stays the
 * quiz authority and relays the render payload here, which the recorder feeds
 * straight into its own hidden RecordingQuizWindow.html. That is why the master
 * builds the same object today; only the destination changes.
 */


/** Query flag that turns the app into a headless recorder. */
export const RECORDER_QUERY_FLAG = 'recorder';

/** userData.role carried by the recorder participant, so peers can filter it out. */
export const RECORDER_ROLE = 'recorder';

export const RecorderEvent = {
    /** recorder → master: "I am here", with my session id. */
    HELLO: 'RECORDER_HELLO',
    /** recorder → master: send me the current quiz + recording state. */
    REQUEST_STATE: 'RECORDER_REQUEST_STATE',
    /** recorder → master: recording started / stopped / failed. */
    STATUS: 'RECORDER_STATUS',
    /** master → recorder: begin recording. */
    START: 'RECORDER_START',
    /** master → recorder: stop and flush to disk. */
    STOP: 'RECORDER_STOP',
    /**
     * master → recorder: full quiz content, in pieces.
     *
     * Daily caps an app message at a few kilobytes. A quiz — questions, options,
     * and possibly image data URIs — goes well past that, so `sendAppMessage`
     * threw and the recorder sat on "waiting for quiz content" forever. The
     * throw was caught and logged at warning level, which is to say invisible.
     *
     * The content is therefore serialised once and sent in chunks, reassembled
     * on the other side. Still once per quiz: the live state travels separately
     * in QUIZ_UPDATE and stays small by construction.
     */
    QUIZ_INIT_CHUNK: 'RECORDER_QUIZ_INIT_CHUNK',
    /** master → recorder: live quiz state. Throttled. Never carries `quiz`. */
    QUIZ_UPDATE: 'RECORDER_QUIZ_UPDATE',
    /**
     * master → recorder: proof of life.
     *
     * The quiz is the only part of the composite riding an application channel,
     * so a silent failure would freeze the quiz in the video while everything
     * else kept moving — the kind of fault you discover on playback, too late.
     */
    HEARTBEAT: 'RECORDER_HEARTBEAT',
    /** master → recorder: master microphone mute/unmute state. */
    MIC_STATE: 'RECORDER_MIC_STATE',
} as const;

export interface RecorderMicStatePayload {
    micOn: boolean;
}

export interface RecorderQuizInitChunkPayload {
    /** Identifies one transfer, so a superseded quiz's chunks are not mixed in. */
    id: string;
    index: number;
    total: number;
    /** A slice of JSON.stringify(quiz). `null` is a valid whole payload. */
    body: string;
}

/**
 * Characters per chunk. Deliberately well under Daily's app-message ceiling:
 * the JSON is escaped and wrapped in an envelope, so the wire size is larger
 * than the slice, and a message that is too big fails the whole transfer.
 */
export const RECORDER_CHUNK_CHARS = 2000;

/**
 * Only what RecordingQuizWindow.html actually reads.
 *
 * The whole session object used to be relayed. It is unbounded — and so was the
 * leaderboard, at roughly 60 bytes per participant, which alone exceeds the
 * app-message limit in a full room. Projecting to the five fields the renderer
 * uses keeps QUIZ_UPDATE small by construction rather than by luck, four times
 * a second.
 */
export interface RecorderQuizSession {
    currentQuestionIndex: number;
    questionStartTime: number | null;
    questionEndTime: number | null;
    questionState: string | null;
    timeLimit: number | null;
}

/** The renderer shows the top three; ten leaves room without risking the cap. */
export const RECORDER_LEADERBOARD_LIMIT = 10;

export interface RecorderQuizUpdatePayload {
    shouldShowQuiz: boolean;
    session: RecorderQuizSession | null;
    leaderboard: { peerId: string; name: string; score: number }[];
    /** Broadcast already, but relayed too so the recorder never waits on ordering. */
    masterCameraOverride: boolean;
    /** Master microphone mute state */
    masterMicOn?: boolean;
}

export interface RecorderStatusPayload {
    isRecording: boolean;
    error?: string;
}

/** Silence longer than this means the relay is down; the recorder logs loudly. */
export const RECORDER_HEARTBEAT_INTERVAL_MS = 2000;
export const RECORDER_HEARTBEAT_TIMEOUT_MS = 8000;

/** Throttle for QUIZ_UPDATE — matches the master's existing iframe postMessage cadence. */
export const RECORDER_QUIZ_UPDATE_THROTTLE_MS = 300;

/** Window name, so re-opening focuses the existing recorder instead of spawning a second. */
export const RECORDER_WINDOW_NAME = 'catevideo-recorder';

export const buildRecorderUrl = (): string => {
    const url = new URL(window.location.href);
    url.searchParams.set(RECORDER_QUERY_FLAG, '1');
    // Never carry the master's credential into a second window (Version 2.md,
    // D12). The recorder joins as an ordinary participant with no token, so it
    // has no use for either form.
    url.searchParams.delete('key');
    url.searchParams.delete('m');
    return url.toString();
};

export const isRecorderWindow = (): boolean => {
    try {
        return new URLSearchParams(window.location.search).get(RECORDER_QUERY_FLAG) === '1';
    } catch {
        return false;
    }
};
